import React from "react";

/**
 * BEFORE/AFTER SECTION – Single self-contained file
 * - Includes: data, image imports, UI components (Showcase, Card, Compare)
 * - Matches your Services design (rounded-2xl, emerald accents, badges, gradient divider)
 * - Accessible: keyboard (←/→), labels, focusable compare area, buttons
 * - No external libs
 *
 * HOW TO USE
 * - Drop this file anywhere (e.g. src/sections/BeforeAfterSection.jsx)
 * - Put your images in /src/assets/before-after/
 * - Update the import paths below to match your project structure
 * - Render <BeforeAfterSection /> in your page
 */

/* ---------------- Image imports (replace with your real files) ---------------- */
// Example structure:
// /src/assets/before-after/kitchen_before.jpg
// /src/assets/before-after/kitchen_after.jpg
// /src/assets/before-after/bath_before.jpg
// /src/assets/before-after/bath_after.jpg

import kitchenBefore from "../../assets/gallery/Before.jpg";
import kitchenAfter from "../../assets/gallery/After.jpg";
import bathBefore from "../../assets/gallery/Before2.jpg";
import bathAfter from "../../assets/gallery/After2.jpg";

/* ---------------- Data (edit freely) ---------------- */
const ITEMS = [
    {
        key: "kitchen",
        tag: "Haus & Wohnen",
        badge: "Neu",
        headline: "Küchenfliesen – Schmutz & Flecken gründlich entfernt",
        before: { src: kitchenBefore, alt: "Küchenfliesen vor der Reinigung" },
        after: { src: kitchenAfter, alt: "Küchenfliesen nach der Reinigung" },
        labelBefore: "Vorher",
        labelAfter: "Nachher",
        bullets: [
            "Gründliche Fugenreinigung",
            "Entfernung hartnäckiger Flecken",
            "Strahlend saubere Fliesen",
        ],
    },
    {
        key: "carpet",
        tag: "Textil & Polster",
        headline: "Teppich – Flecken & Staub beseitigt",
        before: { src: bathBefore, alt: "Teppich vor der Reinigung" },
        after: { src: bathAfter, alt: "Teppich nach der Reinigung" },
        labelBefore: "Vorher",
        labelAfter: "Nachher",
        bullets: [
            "Fleckenentfernung mit Spezialmitteln",
            "Tiefenreinigung der Fasern",
            "Frischer Geruch & gepflegtes Aussehen",
        ],
    },
];

/* ---------------- Default export: ready-to-render section ---------------- */
export default function BeforeAfterSection({
    title = "Vorher/Nachher",
    subtitle = "Ziehen Sie den Regler oder benutzen Sie die Pfeiltasten, um den Unterschied zu sehen.",
    items = ITEMS,
    className = "",
}) {
    return (
        <section id="results" className={["relative bg-white py-16 sm:py-20", className].join(" ")} aria-labelledby="ba-title">
            <div className="mx-auto max-w-7xl px-6 lg:px-12">
                {/* Header */}
                <header className="text-center">
                    <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                        Ergebnisse
                    </span>
                    <h2 id="ba-title" className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-gray-900 sm:text-4xl md:text-5xl">
                        {title}
                    </h2>
                    <p className="mx-auto mt-3 max-w-2xl text-sm text-gray-600 sm:text-base">{subtitle}</p>
                </header>

                {/* Grid */}
                <div className="mt-10 grid grid-cols-1 gap-6 sm:gap-8 md:grid-cols-2">
                    {items.map((it) => (
                        <BeforeAfterCard key={it.key} {...it} />
                    ))}
                </div>


            </div>
        </section>
    );
}

/* ---------------- Card ---------------- */
function BeforeAfterCard({
    tag,
    badge,
    headline,
    before,
    after,
    labelBefore = "Vorher",
    labelAfter = "Nachher",
    bullets = ["Gründliche Oberflächenreinigung", "Sichere Mittel & Methoden", "Sichtbarer Glanz"],
}) {
    return (
        <article className="relative flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition hover:-translate-y-[2px] hover:shadow-md">
            {/* badges line */}
            <div className="mb-4 flex items-center gap-2">
                {badge && (
                    <span className="inline-flex rounded-full bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700 ring-1 ring-inset ring-teal-200">
                        {badge}
                    </span>
                )}
                {tag && (
                    <span className="ml-auto inline-flex rounded-full bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
                        {tag}
                    </span>
                )}
            </div>

            {/* Headline */}
            {headline && <h3 className="text-lg font-bold text-gray-900">{headline}</h3>}

            {/* Divider */}
            <div className="mt-3 h-px w-full bg-gradient-to-r from-transparent via-emerald-200/70 to-transparent" />

            {/* Compare Canvas */}
            <div className="mt-4 overflow-hidden rounded-xl border border-emerald-100 bg-gray-50">
                <Compare
                    before={before}
                    after={after}
                    labelBefore={labelBefore}
                    labelAfter={labelAfter}
                />
            </div>

            {/* Mini bullet list for rhythm */}
            {!!bullets?.length && (
                <ul className="mt-4 grid grid-cols-1 gap-1.5 text-xs text-gray-600">
                    {bullets.map((b, i) => (
                        <li key={i} className="flex items-center gap-2">
                            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                            {b}
                        </li>
                    ))}
                </ul>
            )}
        </article>
    );
}

/* ---------------- Compare (no deps) ---------------- */
function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
}

function Compare({ before, after, labelBefore, labelAfter }) {
    const containerRef = React.useRef(null);
    const [pct, setPct] = React.useState(50); // percentage split
    const [dragging, setDragging] = React.useState(false);
    const [loaded, setLoaded] = React.useState({ before: false, after: false });

    // keyboard control
    const onKeyDown = (e) => {
        if (e.key === "ArrowLeft") setPct((p) => clamp(p - 3, 0, 100));
        if (e.key === "ArrowRight") setPct((p) => clamp(p + 3, 0, 100));
    };

    // pointer/mouse/touch handlers
    const updateFromEvent = (clientX) => {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const x = clamp(clientX - rect.left, 0, rect.width);
        setPct(Math.round((x / rect.width) * 100));
    };

    const onPointerDown = (e) => {
        setDragging(true);
        updateFromEvent(e.clientX);
    };
    const onPointerMove = (e) => {
        if (!dragging) return;
        updateFromEvent(e.clientX);
    };
    const onPointerUp = () => setDragging(false);

    const onTouchStart = (e) => {
        setDragging(true);
        updateFromEvent(e.touches[0].clientX);
    };
    const onTouchMove = (e) => {
        if (!dragging) return;
        updateFromEvent(e.touches[0].clientX);
    };
    const onTouchEnd = () => setDragging(false);

    React.useEffect(() => {
        if (!dragging) return;
        const up = () => setDragging(false);
        window.addEventListener("mouseup", up);
        window.addEventListener("mouseleave", up);
        return () => {
            window.removeEventListener("mouseup", up);
            window.removeEventListener("mouseleave", up);
        };
    }, [dragging]);

    return (
        <div
            ref={containerRef}
            className="relative aspect-[16/10] select-none outline-none"
            role="group"
            aria-roledescription="before after comparer"
            tabIndex={0}
            onKeyDown={onKeyDown}
            onMouseDown={onPointerDown}
            onMouseMove={onPointerMove}
            onMouseUp={onPointerUp}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
        >
            {/* AFTER (base layer) */}
            <img
                src={after?.src}
                alt={after?.alt || "Nachher"}
                className="absolute inset-0 h-full w-full object-cover"
                onLoad={() => setLoaded((s) => ({ ...s, after: true }))}
            />

            {/* BEFORE (clipped) */}
            <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${pct}%` }}
                aria-hidden="true"
            >
                <img
                    src={before?.src}
                    alt={before?.alt || "Vorher"}
                    className="h-full w-full object-cover"
                    onLoad={() => setLoaded((s) => ({ ...s, before: true }))}
                />
            </div>

            {/* Center guide gradient for subtle depth */}
            <div
                className="pointer-events-none absolute inset-y-0"
                style={{ left: `calc(${pct}% - 1px)`, width: 2 }}
            >
                <div className="h-full bg-gradient-to-b from-emerald-300/70 via-emerald-500 to-emerald-300/70" />
            </div>

            {/* Handle */}
            <button
                type="button"
                className="absolute top-1/2 -translate-y-1/2"
                style={{ left: `calc(${pct}% - 18px)` }}
                aria-label={`Vergleichsregler, aktuell bei ${pct}%`}
                title="Ziehen oder mit Pfeiltasten bewegen"
                onMouseDown={onPointerDown}
                onTouchStart={onTouchStart}
            >
                <div className="grid h-9 w-9 place-items-center rounded-full border border-emerald-300 bg-white shadow-md hover:shadow-lg">
                    <ArrowsIcon className="h-4 w-4 text-emerald-700" />
                </div>
            </button>

            {/* Corner labels */}
            <LabelChip text={labelBefore} position="left" />
            <LabelChip text={labelAfter} position="right" />

            {/* Loading veil */}
            {!(loaded.before && loaded.after) && (
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px]" aria-hidden="true" />
            )}
        </div>
    );
}

function LabelChip({ text, position = "left" }) {
    const pos = position === "left" ? "left-3" : "right-3";
    return (
        <span
            className={[
                "pointer-events-none absolute top-3 z-10 inline-flex items-center gap-1 rounded-full",
                "bg-gray-900/80 px-2.5 py-1 text-xs font-semibold text-white shadow",
                pos,
            ].join(" ")}
        >
            <Dot className="h-1.5 w-1.5" /> {text}
        </span>
    );
}

/* ---------------- Icons ---------------- */
function ArrowsIcon({ className = "" }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M8 12H4m0 0l3-3M4 12l3 3" />
            <path d="M16 12h4m0 0l-3-3m3 3l-3 3" />
        </svg>
    );
}

function Dot({ className = "" }) {
    return (
        <svg className={className} viewBox="0 0 6 6" fill="currentColor" aria-hidden="true">
            <circle cx="3" cy="3" r="3" />
        </svg>
    );
}
